import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap, timeout } from 'rxjs/operators';

import { AuthenticationService } from 'app/core/authentication';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { AccountSettingsDataService } from 'app/shared/services/account-settings-data-service';
import { StudentBtnSizeOption } from 'app/student/components/student-btn/student-btn-size-option';
import { StudentBtnTypeOption } from 'app/student/components/student-btn/student-btn-type-option';

const MODAL_HIDE_TIMEOUT_MS = 1000;

@Component({
  template: '',
  standalone: false
})
export abstract class DeleteAccountModal implements OnInit {
  protected readonly bsModalRef = inject(BsModalRef);
  protected readonly translationService = inject(TranslationService);
  protected readonly formBuilder = inject(FormBuilder);
  protected readonly accountSettingsDataService = inject(AccountSettingsDataService);
  protected readonly authenticationService = inject(AuthenticationService);

  public deleteText = '';
  public confirmationText = '';
  public deleteAccountForm!: FormGroup;

  public btnSizes = StudentBtnSizeOption;
  public btnTypes = StudentBtnTypeOption;

  public deleteAccountFormFields = {
    confirmation: 'confirmation'
  };

  public ngOnInit(): void {
    this.deleteText = this.translationService.instant('TEACHER_DELETE_ACCOUNT_MODAL.DELETE');
    this.deleteAccountForm = this.createForm();
  }

  public closeModal() {
    this.bsModalRef.hide();
  }

  public createForm(): FormGroup {
    return this.formBuilder.group({
      [this.deleteAccountFormFields.confirmation]: this.formBuilder.control('', [Validators.required, this.deleteTextValidator])
    });
  }

  public deleteAccount(): void {
    this.accountSettingsDataService.deleteUser()
      .pipe(
        switchMap(() => this.hideModal()),
        tap(() => this.authenticationService.logOut()))
      .subscribe();
  }

  // The modal animates out, so logout must wait or the dialog lands on top of the login page.
  private hideModal(): Observable<void> {
    const hidden$ = this.bsModalRef.onHidden;

    this.bsModalRef.hide();

    if (!hidden$) {
      return of(undefined);
    }

    return hidden$
      .pipe(
        take(1),
        timeout(MODAL_HIDE_TIMEOUT_MS),
        catchError(() => of(undefined)),
        map(() => undefined)
      );
  }

  public getPlaceholder(): string {
    return this.translationService.instant(
      'TEACHER_DELETE_ACCOUNT_MODAL.TYPE_DELETETEXT',
      { deleteText: this.deleteText }
    );
  }

  public isValid() {
    return this.deleteAccountForm.valid;
  }

  private deleteTextValidator = (control: AbstractControl): ValidationErrors | null => {
    if (control !== null) {
      if (control.dirty && control.value && (control.value as string).toUpperCase() !== this.deleteText.toUpperCase()) {
        control.setErrors({ mismatch: true });
      }
      else {
        control.setErrors(null);
      }

      return control.errors;
    }

    return null;
  };
}
